import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { SignDetail } from '../../../model/sign/sign-detail';
import { StockForSign } from '../../../model/sign-stock/stock-for-sign';
import { SignStockControllerRequestService } from '../../../services/api/sign-stock-controller-request.service';
import {
  ModalService,
  ObservableHandlerPair,
} from '../../../services/modal.service';
import { SignStockEditRequestComponent } from './edit/sign-stock-edit-request.component';
import { SignStockEditRequest } from '../../../model/sign-stock/sign-stock-edit-request';
import {
  SignLocationType,
  SignLocationTypesChoices,
} from '../../../model/sign-stock/sign-location-type';
import { SnackService } from '../../../services/snack.service';
import { SignFrame } from '../../../model/sign/enums/sign-frame';
import {
  ProductionStockToPhysicalStockComponent,
  ProductionStockToPhysicalStockEvent,
} from './production-stock-to-physical-stock-modal/production-stock-to-physical-stock-modal.component';
import { SignsControllerRequestService } from 'src/app/services/api/signs-controller-request.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { Subject } from 'rxjs';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { SignStockOfferSentStatusRequest } from '../../../model/sign-stock/sign-stock-offer-sent-status-request';
import {
  RecupMethodChoiceArray,
  SignRecupMethod,
} from '../../../model/sign/enums/sign-recup-method';
import { SignStockSetRecupMethodRequest } from '../../../model/sign-stock/sign-stock-set-recup-method-request';

@Component({
  selector: 'stock-for-sign',
  templateUrl: './stock-for-sign.component.html',
  styleUrls: ['./stock-for-sign.component.scss'],
})
export class StockForSignComponent
  extends SubscriptionBasedComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input()
  sign: SignDetail;

  @Output()
  roofLocationChanged = new EventEmitter<string>();

  @Output()
  recupMethodChanged = new EventEmitter<SignRecupMethod>();

  roofLocation: string;

  isFetching: boolean;

  stockAmountGrandTotal: number;

  //'Magazijn'
  stockAmountWarehouseTotal: number;
  duopropRoof: EditableStockItem;
  duopropDefault: EditableStockItem;
  frameCorner: EditableStockItem;
  pending: EditableStockItem;

  placed: EditableStockItem;
  placementInProgress: EditableStockItem;
  toDisassemble: EditableStockItem;
  deferred: EditableStockItem;
  inProduction: EditableStockItem;
  inOffer: EditableStockItem;
  directOrder: EditableStockItem;

  //Keep track of unsupported stock
  unsupportedPhysicalStock: StockForSign[] = [];
  unsupportedRemainderStock: StockForSign[] = [];

  private roofLocationEditedSubject = new Subject<void>();

  readonly PHYSICAL_LOCATION = SignLocationTypesChoices.PHYSICAL;

  protected readonly RecupMethodChoiceArray = RecupMethodChoiceArray;

  constructor(
    private signStockControllerRequestService: SignStockControllerRequestService,
    private signsService: SignsControllerRequestService,
    private modalService: ModalService,
    private snackService: SnackService
  ) {
    super();
  }

  ngOnInit(): void {
    this.initializeSubscriptions();
    this.initializeRecupMethodDropdown();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.sign) {
      this.initializeRoofLocation();

      this.initializeEditableStockItems();

      this.fetchStock();
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.roofLocationEditedSubject != null) {
      this.roofLocationEditedSubject.complete();
      this.roofLocationEditedSubject = null;
    }
  }

  private initializeSubscriptions(): void {
    this.addSubscription(
      this.roofLocationEditedSubject,
      async () => {
        await this.signsService.updateRoofLocation(
          this.sign.id,
          this.roofLocation
        );
        this.sign.roofLocation = this.roofLocation;
        this.roofLocationChanged.next(this.roofLocation);
        this.snackService.showSuccess('Succesvol opgeslagen');
      },
      GeneralHelper.noop,
      500
    );
  }

  private initializeRoofLocation(): void {
    if (!!this.sign) {
      this.roofLocation = this.sign.roofLocation;
    }
  }

  private initializeEditableStockItems(): void {
    if (this.sign.defaultLocation != null) {
      this.duopropRoof = new EditableStockItem(
        SignLocationTypesChoices.PHYSICAL,
        {
          rack: this.sign.defaultLocation.rack,
          shelf: 'D',
          compartment: 1,
        },
        null
      );
      this.duopropDefault = new EditableStockItem(
        SignLocationTypesChoices.PHYSICAL,
        {
          rack: this.sign.defaultLocation.rack,
          shelf: this.sign.defaultLocation.shelf,
          compartment: this.sign.defaultLocation.compartment,
        },
        null
      );
    }

    this.frameCorner = new EditableStockItem(
      SignLocationTypesChoices.FRAME_CORNER,
      null,
      this.sign.frame
    );
    this.pending = new EditableStockItem(
      SignLocationTypesChoices.PENDING_ORDER,
      null,
      null
    );
    this.placed = new EditableStockItem(
      SignLocationTypesChoices.PLACED,
      null,
      this.sign.frame
    );
    this.placementInProgress = new EditableStockItem(
      SignLocationTypesChoices.PLACEMENT_IN_PROGRESS,
      null,
      this.sign.frame
    );
    this.toDisassemble = new EditableStockItem(
      SignLocationTypesChoices.TO_DISASSEMBLE,
      null,
      this.sign.frame
    );
    this.deferred = new EditableStockItem(
      SignLocationTypesChoices.DEFERRED,
      null,
      null
    );
    this.inProduction = new EditableStockItem(
      SignLocationTypesChoices.IN_PRODUCTION,
      null,
      null
    );
    this.inOffer = new EditableStockItem(
      SignLocationTypesChoices.IN_OFFER,
      null,
      null
    );
    this.directOrder = new EditableStockItem(
      SignLocationTypesChoices.DIRECT_ORDER,
      null,
      null
    );
  }

  fetchStock(): void {
    this.stockAmountGrandTotal = 0;

    this.stockAmountWarehouseTotal = 0;

    if (this.duopropRoof != null) {
      this.duopropRoof.amount = 0;
    }
    if (this.duopropDefault != null) {
      this.duopropDefault.amount = 0;
    }
    this.frameCorner.amount = 0;
    this.pending.amount = 0;
    this.placed.amount = 0;
    this.toDisassemble.amount = 0;
    this.deferred.amount = 0;
    this.inProduction.amount = 0;
    this.inOffer.amount = 0;

    this.unsupportedPhysicalStock = [];
    this.unsupportedRemainderStock = [];

    this.isFetching = true;
    this.signStockControllerRequestService
      .stockForSignWithId(this.sign.id)
      .then((res) => {
        res.forEach((item) => {
          if (item.amount !== 0) {
            this.increaseAmountsForStockItem(item);
          }
        });
      })
      .finally(() => (this.isFetching = false));
  }

  private increaseAmountsForStockItem(item: StockForSign): void {
    switch (item.locationType.value) {
      case SignLocationTypesChoices.PHYSICAL.value:
        this.increaseAmountsForPhysical(item);
        break;
      case SignLocationTypesChoices.IN_PRODUCTION.value:
        if (this.stockItemMatches(item, this.inProduction)) {
          this.inProduction.amount = item.amount;
        } else {
          if (item.amount !== 0) {
            this.unsupportedRemainderStock.push(item);
          }
        }
        break;
      case SignLocationTypesChoices.PLACED.value:
        if (this.stockItemMatches(item, this.placed)) {
          this.placed.amount = item.amount;
        } else {
          if (item.amount !== 0) {
            this.unsupportedRemainderStock.push(item);
          }
        }
        break;
      case SignLocationTypesChoices.PLACEMENT_IN_PROGRESS.value:
        if (this.stockItemMatches(item, this.placementInProgress)) {
          this.placementInProgress.amount = item.amount;
        } else {
          if (item.amount !== 0) {
            this.unsupportedRemainderStock.push(item);
          }
        }
        break;
      case SignLocationTypesChoices.TO_DISASSEMBLE.value:
        if (this.stockItemMatches(item, this.toDisassemble)) {
          this.toDisassemble.amount = item.amount;
        } else {
          if (item.amount !== 0) {
            this.unsupportedRemainderStock.push(item);
          }
        }
        break;
      case SignLocationTypesChoices.DEFERRED.value:
        if (this.stockItemMatches(item, this.deferred)) {
          this.deferred.amount = item.amount;
        } else {
          if (item.amount !== 0) {
            this.unsupportedRemainderStock.push(item);
          }
        }
        break;
      case SignLocationTypesChoices.PENDING_ORDER.value:
        if (this.stockItemMatches(item, this.pending)) {
          //Special case: negative stock is represented as simply the amount with red text
          this.pending.amount = Math.abs(item.amount);
        } else {
          if (item.amount !== 0) {
            this.unsupportedRemainderStock.push(item);
          }
        }
        break;
      case SignLocationTypesChoices.FRAME_CORNER.value:
        this.stockAmountWarehouseTotal += item.amount;
        if (this.stockItemMatches(item, this.frameCorner)) {
          this.frameCorner.amount = item.amount;
        } else {
          if (item.amount !== 0) {
            this.unsupportedRemainderStock.push(item);
          }
        }
        break;
      case SignLocationTypesChoices.IN_OFFER.value:
        if (this.stockItemMatches(item, this.inOffer)) {
          this.inOffer.amount = item.amount;
          this.inOffer.offerDate =
            item.offerDate != null ? new Date(item.offerDate) : null;
          this.inOffer.offerRequester = item.offerRequester
            ? {
                firstName: item.offerRequester.firstName,
                lastName: item.offerRequester.lastName,
              }
            : undefined;
        } else {
          if (item.amount !== 0) {
            this.unsupportedRemainderStock.push(item);
          }
        }
        break;
      case SignLocationTypesChoices.DIRECT_ORDER.value:
        if (this.stockItemMatches(item, this.directOrder)) {
          this.directOrder.amount = item.amount;
          this.directOrder.directOrderDate = item.directOrderDate
            ? new Date(item.directOrderDate)
            : null;
          this.directOrder.directOrderRequester = item.directOrderRequester
            ? {
                firstName: item.directOrderRequester.firstName,
                lastName: item.directOrderRequester.lastName,
              }
            : undefined;
        } else {
          if (item.amount !== 0) {
            this.unsupportedRemainderStock.push(item);
          }
        }
        break;
      default:
        if (item.amount !== 0) {
          this.unsupportedRemainderStock.push(item);
        } //This should never happen
    }

    this.stockAmountGrandTotal =
      this.stockAmountWarehouseTotal - this.pending.amount;
  }

  private increaseAmountsForPhysical(item: StockForSign): void {
    this.stockAmountWarehouseTotal += item.amount;

    if (this.stockItemMatches(item, this.duopropDefault)) {
      this.duopropDefault.amount = item.amount;
    } else if (this.stockItemMatches(item, this.duopropRoof)) {
      this.duopropRoof.amount = item.amount;
    } else {
      if (item.amount !== 0) {
        this.unsupportedPhysicalStock.push(item);
      }
    }
  }

  //TODO why do all of this in the frontend?
  private stockItemMatches(
    item: StockForSign,
    editableStockItem: EditableStockItem
  ): boolean {
    if (item.locationType.value !== editableStockItem.locationType.value) {
      return false;
    }
    if (item.location != null) {
      if (editableStockItem.location == null) {
        return false;
      } else {
        if (
          editableStockItem.location.rack !== item.location.rack ||
          editableStockItem.location.shelf !== item.location.shelf ||
          editableStockItem.location.compartment !== item.location.compartment
        ) {
          return false;
        }
      }
    } else {
      if (editableStockItem.location != null) {
        return false;
      }
    }
    if (
      !this.enumMatches(item.frame, editableStockItem.frame, (t) => t.value)
    ) {
      return false;
    }

    return true;
  }

  private enumMatches<T>(o1: T, o2: T, valueGetter: (t: T) => number) {
    if (o1 == null) {
      if (o2 != null) {
        return false;
      }
    } else {
      if (o2 == null) {
        return false;
      } else {
        if (valueGetter(o1) !== valueGetter(o2)) {
          return false;
        }
      }
    }

    return true;
  }

  private openEditRequestInModal(editRequest: SignStockEditRequest): void {
    this.modalService.openComponentInModal(
      SignStockEditRequestComponent,
      [
        { key: 'editRequest', value: editRequest },
        { key: 'sign', value: this.sign },
      ],
      new ObservableHandlerPair(
        (modalRef) => modalRef.componentInstance.stockEdited,
        () => this.onStockEdited()
      )
    );
  }

  onStockEdited(): void {
    this.fetchStock();
  }

  editDuopropRoof(): void {
    if (
      this.sign.defaultLocation == null ||
      this.sign.defaultLocation.rack == null
    ) {
      this.snackService.showError(
        'Deze voorraad kan niet worden aangepast indien er geen standaard locatie is ingesteld voor het bord'
      );
      return;
    }

    const editRequest = this.constructSignStockEditRequest(this.duopropRoof);

    this.openEditRequestInModal(editRequest);
  }

  editDuopropDefaultLocation(): void {
    if (
      this.sign.defaultLocation == null ||
      this.sign.defaultLocation.rack == null
    ) {
      this.snackService.showError(
        'Deze voorraad kan niet worden aangepast indien er geen standaard locatie is ingesteld voor het bord'
      );
      return;
    }

    const editRequest = this.constructSignStockEditRequest(this.duopropDefault);

    this.openEditRequestInModal(editRequest);
  }

  editOnFrame(): void {
    const editRequest = this.constructSignStockEditRequest(this.frameCorner);

    this.openEditRequestInModal(editRequest);
  }

  roofLocationEdited(): void {
    this.roofLocationEditedSubject.next();
  }

  editPending(): void {
    const editRequest = this.constructSignStockEditRequest(this.pending);

    this.openEditRequestInModal(editRequest);
  }

  editPlaced(): void {
    const editRequest = this.constructSignStockEditRequest(this.placed);

    this.openEditRequestInModal(editRequest);
  }

  editPlacementInProgress(): void {
    const editRequest = this.constructSignStockEditRequest(
      this.placementInProgress
    );

    this.openEditRequestInModal(editRequest);
  }

  editToDisassemble(): void {
    const editRequest = this.constructSignStockEditRequest(this.toDisassemble);

    this.openEditRequestInModal(editRequest);
  }

  editDeferred(): void {
    const editRequest = this.constructSignStockEditRequest(this.deferred);

    this.openEditRequestInModal(editRequest);
  }

  editInProduction(): void {
    const editRequest = this.constructSignStockEditRequest(this.inProduction);

    this.openEditRequestInModal(editRequest);
  }

  editInOffer(): void {
    const editRequest = this.constructSignStockEditRequest(this.inOffer);

    this.openEditRequestInModal(editRequest);
  }

  editDirectOrder(): void {
    const editRequest = this.constructSignStockEditRequest(this.directOrder);
    this.openEditRequestInModal(editRequest);
  }

  setStockOfferSent() {
    const offerSentStatusRequest = new SignStockOfferSentStatusRequest();
    offerSentStatusRequest.signId = this.sign.id;
    offerSentStatusRequest.offerIsSent = this.sign.stockOfferSent;

    this.signsService.setOfferSentStatus(offerSentStatusRequest).then(() => {
      this.snackService.showSuccess('Offerte status succesvol aangepast');
    });
  }

  private constructSignStockEditRequest(
    editableStockItem: EditableStockItem
  ): SignStockEditRequest {
    const editRequest = new SignStockEditRequest();
    editRequest.signId = this.sign.id;

    editRequest.locationType = editableStockItem.locationType;
    if (editableStockItem.location != null) {
      editRequest.rack = editableStockItem.location.rack;
      editRequest.shelf = editableStockItem.location.shelf;
      editRequest.compartment = editableStockItem.location.compartment;
    }
    editRequest.frame = editableStockItem.frame;

    return editRequest;
  }

  openProductionStockToPhysicalStockModal(): void {
    this.modalService.openComponentInModal(
      ProductionStockToPhysicalStockComponent,
      [
        { key: 'signId', value: this.sign.id },
        { key: 'currentProductionStock', value: this.inProduction.amount },
      ],
      new ObservableHandlerPair(
        (dialog) => dialog.componentInstance.didUpdateStock,
        (event: ProductionStockToPhysicalStockEvent) => {
          if (event != null) {
            this.processProductionStockToPhysicalStockEvent(event);
            this.snackService.showSuccess(
              'Stock in productie succesvol verwerkt'
            );
          }
        }
      ),
      { minWidth: 350 }
    );
  }

  // Generic comparison function
  compareWith<T extends { value: number }>(o1: T, o2: T): boolean {
    if (o1 == null && o2 == null) {
      return true;
    } else if (o1 != null && o2 != null) {
      return o1.value === o2.value;
    } else {
      return false;
    }
  }

  // Specific comparison functions using the generic function
  compareRecupMethod = this.compareWith<SignRecupMethod>;

  private processProductionStockToPhysicalStockEvent(
    event: ProductionStockToPhysicalStockEvent
  ): void {
    this.duopropRoof.amount += event.amountRoof;
    this.duopropDefault.amount += event.amountShelf;
    this.inProduction.amount = 0;
    this.stockAmountWarehouseTotal += event.amountRoof + event.amountShelf;
    this.stockAmountGrandTotal =
      this.stockAmountWarehouseTotal - this.pending.amount;
  }

  transferDirectOrderToProduction(): void {
    this.signStockControllerRequestService
      .transferDirectOrderToProduction(this.sign.id)
      .then(() => {
        this.inProduction.amount =
          this.inProduction.amount + this.directOrder.amount;
        this.directOrder.amount = 0;
        this.directOrder.directOrderDate = undefined;
      });
  }

  transferOfferStockToProduction(): void {
    this.signStockControllerRequestService
      .transferOfferToProduction(this.sign.id)
      .then(() => {
        this.inProduction.amount =
          this.inProduction.amount + this.inOffer.amount;
        this.inOffer.amount = 0;
        this.sign.stockOfferSent = false;
        this.inOffer.offerDate = undefined;
      });
  }

  private initializeRecupMethodDropdown() {
    if (this.RecupMethodChoiceArray.length > 0 && !this.sign.recupMethod) {
      //default to recup duoprop
      this.sign.recupMethod = this.RecupMethodChoiceArray[0];
    }
  }

  setRecupMethod() {
    const setRecupMethodDTO = new SignStockSetRecupMethodRequest();
    setRecupMethodDTO.signId = this.sign.id;
    setRecupMethodDTO.recupMethod = this.sign.recupMethod;

    this.signsService.setRecupMethodStatus(setRecupMethodDTO).then(() => {
      this.recupMethodChanged.emit(this.sign.recupMethod);
      this.snackService.showSuccess('Recuperatie methode succesvol aangepast');
    });
  }
}

class EditableStockItem {
  locationType: SignLocationType;
  location: { rack: number; shelf: string; compartment: number };
  frame: SignFrame;

  amount: number;

  offerDate: Date;
  offerRequester?: { firstName: string; lastName: string };
  directOrderDate?: Date;
  directOrderRequester?: { firstName: string; lastName: string };

  constructor(
    locationType: SignLocationType,
    location: { rack: number; shelf: string; compartment: number },
    frame: SignFrame
  ) {
    this.locationType = locationType;
    this.location = location;
    this.frame = frame;

    this.amount = 0;
  }
}
